import { useState, useCallback } from 'react';
import { getTokenFromFirebase } from '../firebase/hooks';
import { getNestConfig } from '../utilities/config';
import { useAuth } from '../auth/context/AuthContext';

export const STORE = '/google-storage';

export const useUploadFileApi = (authToken?: string | null) => {
  const [isUploading, setIsUploading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { nestApi } = getNestConfig();
  const { selectedAccount } = useAuth();

  const upload = useCallback(
    async (url: string, formData: FormData) => {
      setIsUploading(true);
      setHasError(false);

      try {
        const token = await getTokenFromFirebase();

        const headers: Record<string, string> = {
          authorization: `Bearer ${authToken || token}`,
        };

        if (selectedAccount) {
          headers['x-sub-patient-id'] = String(selectedAccount);
        }

        const response = await fetch(`${nestApi}${url}`, {
          method: 'POST',
          mode: 'cors',
          headers: headers,
          body: formData,
        });

        if (!response.ok) throw new Error('Upload failed');

        const data = await response.json();
        return data;
      } catch (err) {
        console.error('Upload error:', err);
        setHasError(true);
        throw err;
      } finally {
        setIsUploading(false);
      }
    },
    [authToken, nestApi, selectedAccount],
  );

  const uploadEncounterFile = (encounterId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return upload(`${STORE}/encounters/${encounterId}`, formData);
  };

  const uploadVonageFile = (file: File, appointmentId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('appointmentId', appointmentId);
    return upload(`${STORE}/upload-vonage-file`, formData);
  };

  const uploadGenericFile = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return upload(`${STORE}/upload`, formData);
  };

  const uploadAppointmentFile = (file: File, location: number) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('location', location.toString());
    return upload(`${STORE}/appointment-diagnostics`, formData);
  };

  const uploadMessagesFile = (
    file: File,
    providerCodexId: string,
    patientCodexId: string,
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    return upload(
      `${STORE}/in-mails/${providerCodexId}/${patientCodexId}`,
      formData,
    );
  };

  const uploadSupportFile = (file: File, userCodexId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    return upload(`${STORE}/support-chats/${userCodexId}`, formData);
  };

  const uploadProductFile = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return upload(`${STORE}/products`, formData);
  };

  const uploadQuestionnaireFile = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return upload(`${STORE}/upload-questionnaire-image`, formData);
  };

  const uploadQuestionnaireDocument = (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return upload(`${STORE}/upload-questionnaire-document`, formData);
  };

  const uploadMedicalRecordsFile = (file: File, patientCodexId: string) => {
    const formData = new FormData();
    formData.append('file', file);
    return upload(`${STORE}/medical-records/${patientCodexId}`, formData);
  };

  const uploadEducationFile = (file: File, isModuleItem = false) => {
    const formData = new FormData();
    formData.append('file', file);
    return upload(`${STORE}/education?isModuleItem=${isModuleItem}`, formData);
  };

  return {
    isUploading,
    hasError,
    uploadEncounterFile,
    uploadVonageFile,
    uploadQuestionnaireFile,
    uploadGenericFile,
    uploadAppointmentFile,
    uploadMessagesFile,
    uploadProductFile,
    uploadMedicalRecordsFile,
    uploadEducationFile,
    uploadSupportFile,
    uploadQuestionnaireDocument,
  };
};
